import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown } from '@fortawesome/free-solid-svg-icons';

export interface FaqItem {
  question: string;
  answer: string;
  category: string;
}

interface FaqAccordionProps {
  faqs?: FaqItem[];
  faq?: FaqItem;
  activeCategory?: string;
}

const FaqAccordion: React.FC<FaqAccordionProps> = ({ faqs, faq, activeCategory = 'all' }) => {
  const [activeIndex, setActiveIndex] = useState<number | null>(null);

  const toggleFaq = (index: number) => {
    setActiveIndex(activeIndex === index ? null : index);
  };

  // Gérer le cas où on passe un seul élément FAQ
  const itemsToRender = faq ? [faq] : faqs || [];

  // Filter FAQs by active category
  const filteredFaqs = activeCategory === 'all' 
    ? itemsToRender 
    : itemsToRender.filter(item => item.category === activeCategory);

  return (
    <div className="space-y-4">
      {filteredFaqs.length === 0 ? (
        <div className="text-center py-8">
          <p className="text-gray-600">
            Aucune question trouvée dans cette catégorie.
          </p>
        </div>
      ) : (
        filteredFaqs.map((item, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: index * 0.05 }}
            className="border border-gray-200 rounded-lg overflow-hidden"
          >
            <button
              className={`w-full text-left p-5 flex justify-between items-center focus:outline-none ${
                activeIndex === index 
                  ? 'bg-primary-dark/10 text-primary-dark' 
                  : 'bg-white text-gray-900 hover:bg-gray-50'
              }`}
              onClick={() => toggleFaq(index)}
              aria-expanded={activeIndex === index}
            >
              <span className="font-semibold pr-8">{item.question}</span>
              <FontAwesomeIcon 
                icon={faChevronDown} 
                className={`transform transition-transform duration-300 ${
                  activeIndex === index ? 'rotate-180' : ''
                }`} 
              />
            </button>
            <AnimatePresence>
              {activeIndex === index && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.3 }}
                  className="overflow-hidden"
                >
                  <div className="p-5 bg-gray-50 text-gray-700">
                    <p>{item.answer}</p>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        ))
      )}
    </div>
  );
};

export default FaqAccordion;
